import GroupTripPartyAdditionSection from './GroupTripPartyAdditionSection';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { groupTripApi } from '../../api/groupTripApi';
import { travelTradeApi } from '../../api/travelTradeApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import DirectMessageButton from '../messages/DirectMessageButton';
import ContentReportDialog from '../social-post/ContentReportDialog';
import GroupTripActivityParticipation from './GroupTripActivityParticipation';
import GroupTripD1Participation from './GroupTripD1Participation';
import GroupTripRuntimeDecisions from './GroupTripRuntimeDecisions';
import GroupTripRuntimeTimeline from './GroupTripRuntimeTimeline';
import GroupTripLateJoinSection from './GroupTripLateJoinSection';
import GroupTripReviewSection from './GroupTripReviewSection';
import GroupTripHostIdentity from './GroupTripHostIdentity';
import MemberSafetyActions from './MemberSafetyActions';
import GroupTripChatButton from './GroupTripChatButton';
import GroupTripDateConflictNotice from './GroupTripDateConflictNotice';
import { ConfirmDialog } from '../../components/ui';
import {
  canContactGroupTripHost,
  canBuyerCancelPaidParticipation,
  canLeaveGroupTrip,
  buildGroupTripApplyPayload,
  groupTripDateConflicts,
  groupTripDateConflictWarnings,
  groupTripErrorMessage,
  isAwaitingPaymentTrade,
  isGroupTripDateConflictError,
  isGroupTripDateConflictRetryable,
  normalizeTravelerList,
  isPaidTrade,
  isBuyerRefundInProgress,
  normalizeGroupTrip,
  occupancyCopy,
  opaqueId,
  tripPhaseCopy,
} from './groupTripModel';
import { lateJoinTravelerNote, publicParticipantOriginLead } from './groupTripLateJoinModel';
import { groupTripParticipantStatusLabel } from './groupTripPresentation';
import './group-trip.css';

export default function PublicGroupTripPage() {
  const { groupTripId } = useParams();
  const [searchParams] = useSearchParams();
  const noticeApplicationId = opaqueId(searchParams.get('applicationId'));
  const { status, isAuthenticated, user } = useAuth();
  const { t, tf } = useTravelTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [trip, setTrip] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [dateConflict, setDateConflict] = useState(null);
  const [applyWarnings, setApplyWarnings] = useState([]);
  const [note, setNote] = useState('');
  const [selfName, setSelfName] = useState('');
  const [companions, setCompanions] = useState([]);
  const [travelers, setTravelers] = useState([]);
  const [rosterReady, setRosterReady] = useState(false);
  const [issuedClaim, setIssuedClaim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [groupTripReportOpen, setGroupTripReportOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [paidCancelConfirmOpen, setPaidCancelConfirmOpen] = useState(false);
  const generation = useRef(0);
  const viewerId = user?.memberId ?? user?.id;

  // 路由元件在不同揪團間重用（例如點「查看原行程」）：換團時清掉上一團的日期提示。
  useEffect(() => {
    setDateConflict(null);
    setApplyWarnings([]);
  }, [groupTripId]);

  const load = useCallback(async () => {
    const token = ++generation.current;
    if (!/^[1-9]\d*$/.test(groupTripId || '')) { setPhase('not-found'); return; }
    setPhase('loading'); setError('');
    try {
      const detail = normalizeGroupTrip(await groupTripApi.getPublic(groupTripId), t);
      if (token !== generation.current) return;
      setTrip(detail);
      setPhase('ready');
    } catch (cause) {
      if (token !== generation.current) return;
      if (cause?.status === 404 || cause?.raw?.key === 'travel.grouptrip.not.found') setPhase('not-found');
      else { setError(groupTripErrorMessage(cause, t)); setPhase('error'); }
    }
  }, [groupTripId, t]);

  useEffect(() => {
    if (status === 'unknown') return undefined;
    load();
    return () => { generation.current += 1; };
  }, [status, isAuthenticated, load]);

  const isHost = trip && viewerId != null && String(viewerId) === String(trip.hostMemberId);
  // 已離團或被移出的帳號只保留公開／自身歷史入口，不展示其他家庭資料或團內操作。
  const isFormerParticipant = !isHost && ['WITHDRAWN', 'REMOVED'].includes(trip?.viewerParticipantStatus);
  const showMemberContent = isAuthenticated && !isFormerParticipant;

  useEffect(() => {
    if (!isAuthenticated || !trip || isFormerParticipant || !groupTripId || typeof groupTripApi.listTravelers !== 'function') {
      setTravelers([]);
      setRosterReady(!isAuthenticated || isFormerParticipant);
      return undefined;
    }
    let cancelled = false;
    setRosterReady(false);
    groupTripApi.listTravelers(groupTripId)
      .then((payload) => {
        if (cancelled) return;
        setTravelers(normalizeTravelerList(payload).items);
        setRosterReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setTravelers([]);
        setRosterReady(false);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, isFormerParticipant, groupTripId, trip]);

  const noticeFocusMatchesViewer = Boolean(
    noticeApplicationId
    && trip?.viewerApplicationId
    && String(noticeApplicationId) === String(trip.viewerApplicationId),
  );
  const showContactHost = canContactGroupTripHost(trip, viewerId);
  const showLeave = canLeaveGroupTrip(trip);
  const participantLead = publicParticipantOriginLead({
    travelers,
    viewerId,
    rosterReady,
    t,
    statusLabel: trip?.viewerParticipantStatus
      ? groupTripParticipantStatusLabel(trip.viewerParticipantStatus)
      : '',
  });
  const paidTrade = isPaidTrade(trip);
  const showPaidMembership = paidTrade && trip?.viewerParticipantStatus === 'ACTIVE';
  const canCancelPaid = canBuyerCancelPaidParticipation(trip);
  const showRefundedWithdrawn = trip?.viewerTravelTradeStatus === 'REFUNDED';
  const approvedWithoutMembership = trip?.viewerApplicationStatus === 'APPROVED' && !showLeave && !showPaidMembership;
  const awaitingPayment = isAwaitingPaymentTrade(trip);
  // Opaque string from route / normalizeGroupTrip — never Number()-coerce snowflakes.
  const reportTargetId = trip?.groupTripId ?? groupTripId;
  const showGroupTripReport = Boolean(trip && !isHost && reportTargetId);

  function requireAuth() {
    if (isAuthenticated) return true;
    navigate('/login', { state: { from: authReturnLocation(location) } });
    return false;
  }

  function openGroupTripReport() {
    if (!requireAuth()) return;
    setGroupTripReportOpen(true);
  }

  async function apply() {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: authReturnLocation(location) } });
      return;
    }
    if (busy) return;
    setBusy(true); setError(''); setDateConflict(null); setApplyWarnings([]);
    try {
      const application = await groupTripApi.apply(groupTripId, buildGroupTripApplyPayload({
        applicationNote: note || null,
        selfDisplayName: selfName,
        companionNames: companions,
      }));
      await load();
      // 申請已成功；重疊的待審核申請只提醒，不當成失敗。
      setApplyWarnings(groupTripDateConflictWarnings(application));
    } catch (cause) {
      if (isGroupTripDateConflictError(cause)) {
        setDateConflict(isGroupTripDateConflictRetryable(cause)
          ? { variant: 'busy', trips: [] }
          : { variant: 'blocked', trips: groupTripDateConflicts(cause) });
      } else {
        setError(groupTripErrorMessage(cause, t, 'travel.groupTrip.applyFailed'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function withdraw() {
    if (!trip?.viewerApplicationId || busy) return;
    setBusy(true); setError(''); setDateConflict(null); setApplyWarnings([]);
    try {
      await groupTripApi.withdraw(groupTripId, trip.viewerApplicationId);
      await load();
    } catch (cause) {
      setError(groupTripErrorMessage(cause, t, 'travel.groupTrip.withdrawFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function leave() {
    if (!showLeave || busy) return;
    setBusy(true); setError(''); setDateConflict(null); setApplyWarnings([]);
    try {
      await groupTripApi.withdrawParticipant(groupTripId);
      setLeaveConfirmOpen(false);
      await load();
    } catch (cause) {
      setError(groupTripErrorMessage(cause, t, 'travel.groupTrip.leaveFailed'));
    } finally {
      setBusy(false);
    }
  }

  function requestLeave() {
    if (!showLeave || busy) return;
    setLeaveConfirmOpen(true);
  }

  async function cancelPaidParticipation() {
    if (!trip?.viewerTravelTradeId || busy || !canCancelPaid) return;
    setBusy(true); setError(''); setRefunding(true); setDateConflict(null); setApplyWarnings([]);
    try {
      const liveTrade = await travelTradeApi.get(trip.viewerTravelTradeId);
      await travelTradeApi.cancel(trip.viewerTravelTradeId, {
        expectedRowVersion: liveTrade?.rowVersion ?? 1,
      });
      setPaidCancelConfirmOpen(false);
      await load();
    } catch (cause) {
      setError(groupTripErrorMessage(cause, t, 'travel.common.actionFailed'));
    } finally {
      setRefunding(false);
      setBusy(false);
    }
  }

  if (status === 'unknown' || phase === 'loading') {
    return <main className="group-trips"><p>{t('travel.common.loading')}</p></main>;
  }
  if (phase === 'not-found') {
    return (
      <main className="group-trips">
        <h1>{t('travel.groupTrip.notVisibleTitle')}</h1>
        <p>{t('travel.groupTrip.notVisibleHint')}</p>
        <Link to="/public/group-trips">{t('travel.groupTrip.backToPublicList')}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    return (
      <main className="group-trips" role="alert">
        <p>{error}</p>
        <button type="button" onClick={load}>{t('travel.common.retry')}</button>
      </main>
    );
  }

  return (
    <main className="group-trips">
      <header className="group-trips__intro">
        <p>{t('travel.groupTrip.publicRecruitment')}</p>
        <h1>{trip.recruitmentTitle}</h1>
        <div className="group-trips__host-block" data-testid="public-group-trip-host">
          <GroupTripHostIdentity host={trip.host} />
        </div>
        <span data-testid="public-group-trip-occupancy">{occupancyCopy(trip, t, tf)}</span>
        {tripPhaseCopy(trip, t, tf) ? <p className="group-trips__hint">{tripPhaseCopy(trip, t, tf)}</p> : null}
        {trip.summary ? <p>{trip.summary}</p> : null}
        {trip.viewerParticipantStatus ? (
          <p>{tf('travel.groupTrip.participantStatus', { status: groupTripParticipantStatusLabel(trip.viewerParticipantStatus) })}</p>
        ) : null}
        {trip.recruitmentStatus === 'CANCELLING' ? (
          <p className="group-trips__hint">{t('travel.groupTrip.cancellingHint')}</p>
        ) : null}
        {trip.recruitmentStatus === 'CANCELLED' ? (
          <p className="group-trips__hint">{t('travel.groupTrip.cancelledHint')}</p>
        ) : null}
        <p className="group-trips__hint">{t('travel.groupTrip.noDirectChatHint')}</p>
        {(isHost || trip.viewerParticipantStatus === 'ACTIVE') ? (
          <GroupTripChatButton groupTripId={trip.groupTripId} />
        ) : null}
        {trip.journeyPublicationId ? (
          <Link to={`/public/journeys/${trip.journeyPublicationId}`}>{t('travel.groupTrip.viewPublicJourney')}</Link>
        ) : null}
        {/* 已離團或被移出的旅伴仍可能是處理協議的必要當事人，因此不限 ACTIVE；可否讀取仍由後端判定。 */}
        {(isHost || trip.viewerParticipantStatus) ? (
          <Link to={`/group-trips/${trip.groupTripId}/settlements`}>行程異動處理協議</Link>
        ) : null}
        {(isHost || trip.viewerParticipantStatus) ? (
          <Link to={`/group-trips/${trip.groupTripId}/actual`} data-testid="group-actual-link">團體實際行程</Link>
        ) : null}
        {!isHost && trip.hostMemberId != null ? (
          <MemberSafetyActions
            targetMemberId={trip.hostMemberId}
            reportTitleKey="travel.safety.reportHost"
            reportLabelKey="travel.safety.reportHost"
            blockLabelKey="travel.safety.blockHost"
          />
        ) : null}
        {showGroupTripReport ? (
          <div className="group-trip-report-actions">
            <button
              type="button"
              className="member-safety-actions__secondary"
              data-testid="public-group-trip-report"
              onClick={openGroupTripReport}
            >
              {t('travel.safety.reportGroupTrip')}
            </button>
          </div>
        ) : null}
      </header>
      {error ? <p className="group-trips__error" role="alert">{error}</p> : null}
      {dateConflict ? <GroupTripDateConflictNotice variant={dateConflict.variant} trips={dateConflict.trips} /> : null}
      {applyWarnings.length > 0 ? <GroupTripDateConflictNotice variant="pending" trips={applyWarnings} /> : null}
      <section
        className={`group-trips__panel${noticeFocusMatchesViewer ? ' is-notice-focus' : ''}`}
        data-testid="public-group-trip-application-panel"
        data-notice-focus={noticeFocusMatchesViewer ? 'true' : 'false'}
        data-notice-application-id={noticeApplicationId || undefined}
      >
        {isHost ? (
          <Link to={`/group-trips/${trip.groupTripId}`}>{t('travel.groupTrip.hostManageLink')}</Link>
        ) : trip.viewerApplicationStatus === 'PENDING' ? (
          <div className="group-trips__actions group-trips__actions--pending">
            <p className="group-trips__apply-status">{t('travel.groupTrip.pendingApplication')}</p>
            <p className="group-trips__hint">{t('travel.groupTrip.pendingApplicationHint')}</p>
            <button type="button" disabled={busy} onClick={withdraw}>{t('travel.groupTrip.withdrawApplication')}</button>
            {showContactHost ? (
              <DirectMessageButton peerMemberId={trip.hostMemberId} label={t('travel.groupTrip.contactHost')} />
            ) : null}
          </div>
        ) : showRefundedWithdrawn ? (
          <div className="group-trips__actions">
            <p>{t('travel.groupTrip.buyerRefundedWithdrawn')}</p>
          </div>
        ) : showPaidMembership ? (
          <div className="group-trips__actions">
            {participantLead ? <p data-testid="public-participant-origin">{participantLead}</p> : null}
            <p>{t('travel.groupTrip.paidJoined')}</p>
            {isBuyerRefundInProgress(trip, refunding) ? (
              <p className="group-trips__hint">{t('travel.groupTrip.buyerRefundProcessing')}</p>
            ) : null}
            {canCancelPaid ? (
              <p className="group-trips__hint">訂金與付款稍後再提供，這裡先不能操作。</p>
            ) : (
              <p className="group-trips__hint">{t('travel.groupTrip.buyerCancelAfterStart')}</p>
            )}
            {showContactHost ? (
              <DirectMessageButton peerMemberId={trip.hostMemberId} label={t('travel.groupTrip.contactHost')} />
            ) : null}
          </div>
        ) : showLeave ? (
          <div className="group-trips__actions">
            {participantLead ? <p data-testid="public-participant-origin">{participantLead}</p> : null}
            <button type="button" disabled={busy} onClick={requestLeave}>{t('travel.groupTrip.leave')}</button>
            {showContactHost ? (
              <DirectMessageButton peerMemberId={trip.hostMemberId} label={t('travel.groupTrip.contactHost')} />
            ) : null}
          </div>
        ) : awaitingPayment ? (
          <div className="group-trips__actions">
            <p>{t('travel.groupTrip.approvedAwaitingPayment')}</p>
            <p className="group-trips__hint">{t('travel.groupTrip.usePlatformWallet')}</p>
            <p className="group-trips__hint">訂金與付款稍後再提供，這裡先不能操作。</p>
            <button type="button" disabled={busy} onClick={withdraw}>{t('travel.groupTrip.abandonQualification')}</button>
            {showContactHost ? (
              <DirectMessageButton peerMemberId={trip.hostMemberId} label={t('travel.groupTrip.contactHost')} />
            ) : null}
          </div>
        ) : paidTrade ? (
          <div className="group-trips__actions">
            <p>{t('travel.groupTrip.paymentConfirmedJoining')}</p>
            <p className="group-trips__hint">訂金與付款稍後再提供，這裡先不能操作。</p>
          </div>
        ) : approvedWithoutMembership ? (
          <div className="group-trips__actions">
            <p>{t('travel.groupTrip.approvedAwaitingPaymentNoSeat')}</p>
            {trip.recruitmentStatus === 'OPEN' ? (
              <p className="group-trips__hint">訂金與付款稍後再提供，這裡先不能操作。</p>
            ) : (
              <p className="group-trips__hint">{t('travel.groupTrip.recruitmentClosedHint')}</p>
            )}
            <button type="button" disabled={busy} onClick={withdraw}>{t('travel.groupTrip.abandonQualification')}</button>
            {showContactHost ? (
              <DirectMessageButton peerMemberId={trip.hostMemberId} label={t('travel.groupTrip.contactHost')} />
            ) : null}
          </div>
        ) : (
          <div className="group-trips__actions group-trips__actions--apply">
            <label>{t('travel.groupTrip.partySelf')}
              <input
                value={selfName}
                onChange={(event) => setSelfName(event.target.value)}
                maxLength={40}
                data-testid="public-group-trip-self-name"
              />
            </label>
            <p data-testid="public-group-trip-party-size">
              {tf('travel.groupTrip.partySize', { count: 1 + companions.filter((name) => name.trim()).length })}
            </p>
            {companions.map((name, index) => (
              <label key={`companion-${index}`}>
                {t('travel.groupTrip.partyCompanion')}
                <input
                  value={name}
                  maxLength={40}
                  data-testid={`public-group-trip-companion-${index}`}
                  onChange={(event) => {
                    const next = companions.slice();
                    next[index] = event.target.value;
                    setCompanions(next);
                  }}
                />
                <button
                  type="button"
                  onClick={() => setCompanions(companions.filter((_, item) => item !== index))}
                >
                  {t('travel.groupTrip.removeCompanion')}
                </button>
              </label>
            ))}
            <button
              type="button"
              data-testid="public-group-trip-add-companion"
              onClick={() => setCompanions(companions.concat(['']))}
            >
              {t('travel.groupTrip.addCompanion')}
            </button>
            <label>{t('travel.groupTrip.applyNoteLabel')}
              <input value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} />
            </label>
            <button
              type="button"
              className="group-trips__apply-primary"
              data-testid="public-group-trip-apply"
              disabled={busy}
              onClick={apply}
            >
              {t('travel.groupTrip.apply')}
            </button>
            {trip.availableSlots < 1 ? (
              <p
                className="group-trips__hint group-trips__hint--capacity"
                data-testid="public-group-trip-no-slots-hint"
              >
                {t('travel.groupTrip.noSlotsHint')}
              </p>
            ) : null}
          </div>
        )}
      </section>
      {showMemberContent && travelers.length > 0 ? (
        <section className="group-trips__panel" data-testid="public-traveler-roster">
          <h2>{t('travel.groupTrip.travelersHeading')}</h2>
          <ul className="group-trips__list">
            {travelers.map((traveler) => (
              <li className="group-trips__card" key={traveler.travelerId}>
                <p>{traveler.displayName} · {traveler.guest ? t('travel.groupTrip.guestLabel') : t('travel.groupTrip.linkedLabel')}</p>
                {lateJoinTravelerNote(traveler, tf) ? (
                  <p>{lateJoinTravelerNote(traveler, tf)}</p>
                ) : null}
                {traveler.guest && traveler.status === 'ACTIVE' ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true); setDateConflict(null); setApplyWarnings([]);
                      try {
                        const issued = await groupTripApi.issueTravelerClaim(groupTripId, traveler.travelerId);
                        setIssuedClaim(issued);
                      } catch (cause) {
                        setError(groupTripErrorMessage(cause, t, 'travel.common.actionFailed'));
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {t('travel.groupTrip.issueClaim')}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {issuedClaim?.claimToken ? (
            <p data-testid="traveler-claim-token">
              {t('travel.groupTrip.claimTokenHint')}
              {' '}
              <Link to={`/group-trips/claim#${encodeURIComponent(issuedClaim.claimToken)}`}>
                {t('travel.groupTrip.claimAccept')}
              </Link>
            </p>
          ) : null}
        </section>
      ) : null}
      {/* 已離團者沒有途中加入資格；保留既有合法重新申請與自身歷史入口。 */}
      {isAuthenticated && !isHost && !isFormerParticipant ? (
        <GroupTripLateJoinSection
          groupTripId={trip.groupTripId}
          role="candidate"
          viewerId={viewerId}
          hostMemberId={trip.hostMemberId}
          travelers={travelers}
          onChanged={load}
        />
      ) : null}
      {showMemberContent && <GroupTripPartyAdditionSection groupTripId={trip.groupTripId} viewerId={viewerId} travelers={travelers} isHost={isHost} onChanged={load} />}
      {showMemberContent ? <GroupTripActivityParticipation groupTripId={groupTripId} /> : null}
      {showMemberContent ? <GroupTripD1Participation groupTripId={groupTripId} /> : null}
      {showMemberContent ? <GroupTripRuntimeDecisions groupTripId={groupTripId} /> : null}
      {showMemberContent ? (
        <GroupTripRuntimeTimeline groupTripId={groupTripId} hostMemberId={trip.hostMemberId} />
      ) : null}
      {/* ── 評價區塊（行程結束後才顯示） ── */}
      {trip.tripPhase === 'COMPLETED' && (
        <GroupTripReviewSection
          groupTripId={groupTripId}
          hostMemberId={trip.hostMemberId}
          tripPhase={trip.tripPhase}
          visibility={trip.visibility}
        />
      )}
      {groupTripReportOpen ? (
        <ContentReportDialog
          open
          targetType="GROUP_TRIP"
          targetId={reportTargetId}
          titleKey="travel.safety.reportGroupTrip"
          onClose={() => setGroupTripReportOpen(false)}
        />
      ) : null}

      <ConfirmDialog
        open={leaveConfirmOpen}
        title={t('travel.groupTrip.leave')}
        description="離開後將退出此揪團。"
        confirmLabel={t('travel.groupTrip.leave')}
        cancelLabel={t('travel.safety.cancel')}
        danger
        loading={busy}
        onCancel={() => {
          if (!busy) setLeaveConfirmOpen(false);
        }}
        onConfirm={leave}
      />

      <ConfirmDialog
        open={paidCancelConfirmOpen}
        title={t('travel.groupTrip.cancelPaidParticipation')}
        description={t('travel.groupTrip.cancelPaidConfirm')}
        confirmLabel={t('travel.groupTrip.cancelPaidParticipation')}
        cancelLabel={t('travel.safety.cancel')}
        danger
        loading={busy}
        onCancel={() => {
          if (!busy) setPaidCancelConfirmOpen(false);
        }}
        onConfirm={cancelPaidParticipation}
      />
    </main>
  );
}
